import { TanneryListFilterBase } from './tannery-paging.models';
import { GradeMaster } from './tannery-grading.models';
import {
  CustomerTolerance,
  ExportProfile,
  OutwardMaterialState,
  ToleranceMeasure,
} from './tannery-outward-movement.models';

/**
 * The five Phase 1 masters, plus Equipment (Sprint 6), Grade (Sprint 9,
 * design D4) and Sprint 21's two — the drums, paddles and other machines a
 * production order's stage plan assigns, the tenant's own grade vocabulary,
 * the export identity a consignment leaves under, and what each customer has
 * agreed to accept.
 *
 * The keys are the route segment the API expects, so they match
 * PmsTanneryBusiness.ViewModels.TanneryMasterKind exactly. Renaming one here
 * without renaming it there produces a 400 that reads like a missing feature.
 */
export type TanneryMasterKind =
  | 'hidetype'
  | 'stageconfig'
  | 'finishtype'
  | 'tanningmethod'
  | 'qctest'
  | 'equipment'
  | 'grade'
  | 'exportprofile'
  | 'customertolerance';

/**
 * The permission module for one kind — mirrors
 * PmsTanneryBusiness.Services.TanneryMasterService.ModuleByKind exactly, for
 * error messages that need to name the exact responsibility to ask for.
 * Display-only: the server is the one that actually enforces it.
 */
export const MASTER_PERMISSION_MODULE: Record<TanneryMasterKind, string> = {
  hidetype: 'Master - Hide Type',
  stageconfig: 'Master - Stage Config',
  finishtype: 'Master - Finish Type',
  tanningmethod: 'Master - Tanning Method',
  qctest: 'Master - QC Test',
  equipment: 'Master - Equipment',
  grade: 'Master - Grade',
  exportprofile: 'Master - Export Profile',
  customertolerance: 'Master - Customer Tolerance',
};

/** Fields every master carries. Audit values are display-only. */
export interface TanneryMasterBase {
  Id: number;
  Code: string;
  Name: string;
  IsActive: boolean;
  CreatedAt?: string;
  CreatedBy?: string;
  UpdatedAt?: string;
  UpdatedBy?: string;
}

export interface HideType extends TanneryMasterBase {
  Abbreviation: string;
  OriginCountry?: string;
  AvgWeightKg?: number;
  AvgAreaSqft?: number;
  Category?: string;
}

export interface StageConfig extends TanneryMasterBase {
  Phase: string;
  Sequence: number;
  IsGradingGate: boolean;
  /** Sprint 8 design D1: a recipe is charged to the drum or a finish is applied to the surface. Read live at Approve and at stage Start (D2) — never snapshotted onto a batch. */
  UsesChemicals: boolean;
  DefaultDurationHours?: number;
  /** Catalogue-level fallback for SpecSheetStage.RestDurationOverrideHours — same pattern as DefaultDurationHours. */
  DefaultRestDurationHours?: number;
  /** "min-max", "min – max" or a single number (a setpoint) — `tryParseTargetRange` (R10). NULL/blank is not a range to refuse. */
  TemperatureRange?: string;
  PhRange?: string;
  RpmRange?: string;
}

export interface FinishType extends TanneryMasterBase {
  Abbreviation: string;
}

export interface TanningMethod extends TanneryMasterBase {
  Abbreviation: string;
  ChemistryType: 'Chrome' | 'NonChrome' | string;
}

export interface QcTest extends TanneryMasterBase {
  Unit?: string;
  Method?: string;
}

/**
 * Mirrors `EquipmentVm`. MinFillRatio/MaxFillRatio always carry a value (the
 * server defaults them to 0.85/1.00); every other field is optional the way
 * the wire type allows.
 */
export interface Equipment extends TanneryMasterBase {
  Type: string;
  Capacity?: number;
  CapacityUnit?: string;
  MinFillRatio: number;
  MaxFillRatio: number;
  /** SimpleRate | PowerBased, or absent. Read by Sprint 11's costing, not Sprint 6. */
  CostingMethod?: string;
  PowerRatingKW?: number;
  RatePerHour?: number;
  RatePerKWH?: number;
}

/**
 * `tannery.ExportProfile` on the master framework. Named with the `Master`
 * suffix because the plain row type is already exported (it is what an outward
 * movement's detail carries); this is the same row plus the base fields every
 * master screen reads.
 *
 * `Code`, `Name` and `IsActive` are derived server-side — the table has none of
 * those columns — so the form never edits them: `FinancialYear` is the key, and
 * there is no active/inactive state to switch.
 */
export interface ExportProfileMaster extends TanneryMasterBase, ExportProfile {}

/**
 * `tannery.CustomerTolerance` on the master framework — what one customer has
 * agreed for one measure at one material state (ADR-037 level 2 of four). One
 * live row per (customer, state, measure). Suffixed for the same reason
 * `ExportProfileMaster` is: the plain row type is already exported.
 *
 * `IsActive` here is the negation of the row's `IsDeleted`: switching a
 * tolerance off stops it being applied and frees the slot for a renegotiated
 * one, without erasing what was agreed before. `Code`/`Name` are derived
 * server-side for display; the table has neither column. Only WetBlue, Crust
 * and Finished are offered for `MaterialState` — `CK_CustomerTolerance_MaterialState`
 * excludes ByProduct, although the shared row type's union allows it.
 */
export interface CustomerToleranceMaster extends TanneryMasterBase, CustomerTolerance {
  /** Joined on read, ignored on write. */
  CustomerName?: string;
}

export type TanneryMasterRow =
  | HideType
  | StageConfig
  | FinishType
  | TanningMethod
  | QcTest
  | Equipment
  | GradeMaster
  | ExportProfileMaster
  | CustomerToleranceMaster;

/** Narrows GET masters/{entity} to active rows only, or a free-text search over Code and Name — send only what is set. */
export interface MasterListFilter extends TanneryListFilterBase {
  activeOnly?: boolean;
}

/** Shape the API returns from a save. Message is written for a person to read. */
export interface TanneryMasterSaveResult {
  Success: boolean;
  Message: string | null;
  Id: number;
}

/** View/Add/Edit for one {@link TanneryMasterKind} — one entry per kind in {@link TanneryPermissions.Masters}. */
export interface MasterKindPermission {
  View: boolean;
  Add: boolean;
  Edit: boolean;
}

export interface TanneryPermissions {
  /**
   * One entry per {@link TanneryMasterKind} value (including `equipment`) — the
   * permission-naming migration split the single bundled `Tannery - Masters` grant
   * into one module per kind (`Master - Hide Type`, `Master - Equipment`, ...), so
   * a role's access no longer collapses to two flat flags.
   */
  Masters: Record<string, MasterKindPermission>;
  ArticleView: boolean;
  ArticleAdd: boolean;
  ArticleEdit: boolean;
  RecipeView: boolean;
  RecipeAdd: boolean;
  RecipeEdit: boolean;
  SpecSheetView: boolean;
  SpecSheetAdd: boolean;
  SpecSheetEdit: boolean;
  SpecSheetApprove: boolean;
  IssueAdd: boolean;
  SaleOrderView: boolean;
  SaleOrderAdd: boolean;
  SaleOrderEdit: boolean;
  SaleOrderApprove: boolean;
  SaleOrderAccountsApprove: boolean;
  ProductionOrderView: boolean;
  ProductionOrderAdd: boolean;
  ProductionOrderEdit: boolean;
  ProductionOrderApprove: boolean;
  BatchView: boolean;
  BatchAdd: boolean;
  BatchEdit: boolean;
  /**
   * Sprint 8 design D15: `Production - Stage Execution` Edit — stage Start /
   * Complete / Skip, and every write on the stage detail page (chemical
   * additions, instructions, quality readings, clock in/out, downtime).
   * Moved off `BatchEdit`, which now gates only the batch-level actions
   * (Hold/Resume/Complete/Reject).
   */
  StageExecutionEdit: boolean;
  /** Sprint 9 design D13: `Production - Grading`. View reads gates and results; Add saves a Draft; Approve posts it. */
  GradingView: boolean;
  GradingAdd: boolean;
  GradingApprove: boolean;
  /** Sprint 9 design D13: `Production - Output Receipt`. View reads pending outputs, packages and the leather stock list; Edit receives into stock. */
  OutputReceiptView: boolean;
  OutputReceiptEdit: boolean;

  // Sprint 10. ForecastApprove gates BOTH Confirm and purchase-order creation:
  // the first freezes the numbers procurement buys against, the second spends.
  ForecastView: boolean;
  ForecastAdd: boolean;
  ForecastEdit: boolean;
  ForecastApprove: boolean;

  // Sprint 12. DrumScheduleApprove is the reduced-capacity sign-off (ADR-015) —
  // it does not gate Add/Edit, which is any planner scheduling a batch normally.
  DrumScheduleView: boolean;
  DrumScheduleAdd: boolean;
  DrumScheduleEdit: boolean;
  DrumScheduleApprove: boolean;

  // Sprint 13. BatchCostingApprove is required to Post a Draft; Edit reviews
  // and overrides one, and also gates the CostingRate/OverheadAllocationRule
  // master-data screen (no separate responsibility for that screen).
  BatchCostingView: boolean;
  BatchCostingAdd: boolean;
  BatchCostingEdit: boolean;
  BatchCostingApprove: boolean;

  // Sprint 14. A read-only derived dashboard: only View and Acknowledge exist,
  // no Add/Edit.
  LiveMonitorView: boolean;
  LiveMonitorAcknowledge: boolean;

  // Sprint 15. Standard View/Add/Edit — no Approve, nothing here needs
  // commercial sign-off.
  TraceabilityView: boolean;
  TraceabilityAdd: boolean;
  TraceabilityEdit: boolean;

  // Sprint 15a Phase 0. A read-only aggregate dashboard: only View exists,
  // same as Live Monitor's own View flag.
  DashboardView: boolean;

  // Sprint 19. Sample Request is its own top-level screen (unlike Sprints
  // 17/18, which reuse an existing module), so it gets a full module of its
  // own: Add creates/submits a request, Edit starts the pilot (links a
  // batch), Approve records the customer's approval AND rejects the flow —
  // both are the customer-facing sign-off decision. Server-owned by the
  // backend agent's `Sample` module; these four names must match
  // its `TannerySampleFunction.cs`/seed script exactly.
  SampleView: boolean;
  SampleAdd: boolean;
  SampleEdit: boolean;
  SampleApprove: boolean;

  // Sprint 16. A chat feature has nothing to Add/Edit/Approve — only View gates it.
  ChatView: boolean;

  // Sprint 20 (ADR-037). `Dispatch - Outward Movement`, server-owned by
  // `OutwardMovementTransitions.Module` / `TanneryOutwardMovementService`.
  // Edit gates Submit and Cancel; Approve gates Approve and Reject — the same
  // two-tier split a sale order's own actions use. There is no GateOut flag:
  // gate-out is worked from the platform's common gate module, never from
  // this screen (plan §11).
  OutwardMovementView: boolean;
  OutwardMovementAdd: boolean;
  OutwardMovementEdit: boolean;
  OutwardMovementApprove: boolean;

  // Sprint 21 (ADR-037). `Dispatch - Inward Return`. Edit gates Submit and
  // Cancel; Approve gates Approve and Reject, the same two-tier split the
  // outward movement's own flags use — Approve is the stock restore.
  InwardReturnView: boolean;
  InwardReturnAdd: boolean;
  InwardReturnEdit: boolean;
  InwardReturnApprove: boolean;

  // Sprint 22. `Dispatch - Job Work`. Edit gates Send and Cancel on the order
  // itself.
  JobWorkView: boolean;
  JobWorkAdd: boolean;
  JobWorkEdit: boolean;
  // Sprint 23: receiving hides back from a job worker rides the same module.
  // Add/Edit gate a receipt's own Draft (create/update); Approve gates Post —
  // the act that deducts real chemical stock and, on the last instalment,
  // closes the order — so it fails closed with every other Approve flag here.
  JobWorkApprove: boolean;

  // Sprint 4b. `Sales - Sale Order Payment`, the customer receivables ledger.
  // Standard View/Add/Edit — no Approve, an entry is a plain record of money
  // that moved (or was invoiced/credited), not something needing commercial
  // sign-off, and correcting a mistyped receipt is Edit.
  PaymentView: boolean;
  PaymentAdd: boolean;
  PaymentEdit: boolean;

  /**
   * One entry per report entity name (`production-summary`, ...) — the
   * permission-naming migration split the single bundled `Tannery - Reports`
   * grant into one module per report (`Reports - Production Summary`, ...),
   * matching Masters' own per-kind split.
   */
  Reports: Record<string, boolean>;

  /**
   * Sprint 25. Shade cards are a master (`Master - Shade Card`); lab tests are floor quality
   * data and sit beside grading (`Production - Lab Test`).
   *
   * `ShadeAssign` is NOT a module of its own — it reports `Production - Grading` **Add**,
   * because assigning a shade is the same person at the same bench in the same step as
   * grading, and Grading carries no Edit responsibility anywhere to hang a separate one on.
   */
  ShadeCardView: boolean;
  ShadeCardAdd: boolean;
  ShadeCardEdit: boolean;
  ShadeCardApprove: boolean;
  ShadeAssign: boolean;

  LabTestView: boolean;
  LabTestAdd: boolean;
  LabTestEdit: boolean;
  LabTestApprove: boolean;

}

/**
 * ProductionPhase, stored as PascalCase identifiers and shown with these labels
 * (DB-Schema §1.4). The stored value never changes when a label does.
 */
export const PRODUCTION_PHASES: { value: string; label: string }[] = [
  { value: 'Beamhouse', label: 'Beamhouse' },
  { value: 'Tanning', label: 'Tanning' },
  { value: 'WetFinishing', label: 'Wet Finishing' },
  { value: 'DryingConditioning', label: 'Drying & Conditioning' },
  { value: 'Finishing', label: 'Finishing' },
  { value: 'Dispatch', label: 'Dispatch' },
];

export const CHEMISTRY_TYPES: { value: string; label: string }[] = [
  { value: 'Chrome', label: 'Chrome' },
  { value: 'NonChrome', label: 'Chrome-free' },
];

/** The nine `CK_Equipment_Type` values (`EquipmentType` on the server), stored as PascalCase identifiers. */
export const EQUIPMENT_TYPES: { value: string; label: string }[] = [
  { value: 'Drum', label: 'Drum' },
  { value: 'Paddle', label: 'Paddle' },
  { value: 'Spray', label: 'Spray' },
  { value: 'Toggle', label: 'Toggle' },
  { value: 'Buffing', label: 'Buffing' },
  { value: 'Splitting', label: 'Splitting' },
  { value: 'Shaving', label: 'Shaving' },
  { value: 'Measuring', label: 'Measuring' },
  { value: 'Other', label: 'Other' },
];

/**
 * The three `CK_CustomerTolerance_MaterialState` values. Deliberately not
 * `OUTWARD_MATERIAL_STATE_ALL`, which also carries ByProduct: a by-product is a
 * state a consignment can leave in, but never one a tolerance is agreed at.
 */
export const TOLERANCE_MATERIAL_STATES: { value: string; label: string }[] = [
  { value: OutwardMaterialState.WetBlue, label: 'Wet blue' },
  { value: OutwardMaterialState.Crust, label: 'Crust' },
  { value: OutwardMaterialState.Finished, label: 'Finished' },
];

/** The three `CK_CustomerTolerance_Measure` values, as a master form's options. */
export const TOLERANCE_MEASURES: { value: string; label: string }[] = [
  { value: ToleranceMeasure.Quantity, label: 'Quantity' },
  { value: ToleranceMeasure.Measurement, label: 'Measurement' },
  { value: ToleranceMeasure.GradeMix, label: 'Grade mix' },
];

/** The two `CK_Equipment_CostingMethod` values. Read by Sprint 11's costing, not Sprint 6. */
export const EQUIPMENT_COSTING_METHODS: { value: string; label: string }[] = [
  { value: 'SimpleRate', label: 'Simple rate' },
  { value: 'PowerBased', label: 'Power-based' },
];

export function phaseLabel(value: string): string {
  return PRODUCTION_PHASES.find((p) => p.value === value)?.label ?? value;
}

/**
 * The prototype's phase palette (kanzenflow-docs tannery-prototype
 * `leather.enums.ts`), keyed by the stored phase identifier. Every screen
 * that colours a phase — recipe and spec sheet lists, the spec sheet drawer
 * and form, stage config, production order stages — reads it from here.
 */
export const PRODUCTION_PHASE_COLORS: Record<string, string> = {
  Beamhouse: '#1890ff',
  Tanning: '#722ed1',
  WetFinishing: '#13c2c2',
  DryingConditioning: '#fa8c16',
  Finishing: '#52c41a',
  Dispatch: '#eb2f96',
};

const DEFAULT_PHASE_COLOR = PRODUCTION_PHASE_COLORS['Beamhouse'];

export function phaseColor(value: string | null | undefined): string {
  return (value && PRODUCTION_PHASE_COLORS[value]) || DEFAULT_PHASE_COLOR;
}

/**
 * The tag colour of a versioned master's status (recipes and spec sheets
 * share the Draft → Active → Archived lifecycle): Active green, Draft grey,
 * Archived orange — the prototype's mapping, applied everywhere.
 */
export function versionStatusColor(status: string | null | undefined): string {
  switch (status) {
    case 'Active':
      return 'green';
    case 'Archived':
      return 'orange';
    default:
      return 'default';
  }
}
